import type { AutomationCausation } from '@streamlain/automation-safety-contract';

type HistoryStatusI18n = {
  'com.streamlain.streamlain.history.queued': () => string;
  'com.streamlain.streamlain.history.running': () => string;
  'com.streamlain.streamlain.history.status-completed': () => string;
  'com.streamlain.streamlain.history.status-failed': () => string;
  'com.streamlain.streamlain.history.status-cancelled': () => string;
  'com.streamlain.streamlain.history.error-generic': () => string;
};

export type HistoryExecutionStatus =
  | 'running'
  | 'awaiting_external'
  | 'completed'
  | 'failed'
  | 'cancelled';

export type HistoryProcessStatus = 'running' | 'queued' | 'success' | 'error';

/** Wait for a runner pod and run time of a finished run, in ms. */
export interface HistoryRunTimes {
  /** Null when the run found a pod at once. */
  waitMs: number | null;
  runMs: number;
}

/**
 * Splits the time of a finished run into its wait for a runner pod and its
 * run time (tsk-am-wait-in-log): a waiting run does not run, so its wait
 * never counts as run time. Null while the run has not ended.
 */
export function historyRunTimes(log: {
  startedAt: number;
  completedAt?: number;
  queueWaitMs?: number;
}): HistoryRunTimes | null {
  if (!log.completedAt) return null;
  const totalMs = Math.max(0, log.completedAt - log.startedAt);
  if (!log.queueWaitMs) return { waitMs: null, runMs: totalMs };
  const waitMs = Math.min(log.queueWaitMs, totalMs);
  return { waitMs, runMs: totalMs - waitMs };
}

export function formatHistoryDurationMs(durationMs: number): string {
  if (durationMs < 1000) return `${durationMs}ms`;
  if (durationMs < 60000) return `${(durationMs / 1000).toFixed(1)}s`;
  const minutes = Math.floor(durationMs / 60000);
  const seconds = Math.floor((durationMs % 60000) / 1000);
  return `${minutes}m ${seconds}s`;
}

export function formatHistoryExecutionStatusLabel(
  t: HistoryStatusI18n,
  status: HistoryExecutionStatus,
  queued = false
): string {
  if (queued || status === 'awaiting_external') {
    return t['com.streamlain.streamlain.history.queued']();
  }

  switch (status) {
    case 'running':
      return t['com.streamlain.streamlain.history.running']();
    case 'completed':
      return t['com.streamlain.streamlain.history.status-completed']();
    case 'failed':
      return t['com.streamlain.streamlain.history.status-failed']();
    case 'cancelled':
      return t['com.streamlain.streamlain.history.status-cancelled']();
  }
}

export function formatHistoryProcessStatusLabel(
  t: HistoryStatusI18n,
  status: HistoryProcessStatus
): string {
  switch (status) {
    case 'queued':
      return t['com.streamlain.streamlain.history.queued']();
    case 'running':
      return t['com.streamlain.streamlain.history.running']();
    case 'success':
      return t['com.streamlain.streamlain.history.status-completed']();
    case 'error':
      return t['com.streamlain.streamlain.history.status-failed']();
  }
}

/** The plan limit on concurrent runs a queued run waits for. */
export interface HistoryWaitingLimit {
  limit: 'workspace_concurrency' | 'webhook_concurrency';
  current: number;
  max: number;
}

/** Reads the limit the server wrote into the response of a queued run. */
export function readHistoryWaitingLimit(
  response: Record<string, unknown> | undefined
): HistoryWaitingLimit | null {
  if (response?.['status'] !== 'queued') return null;
  const value = response['waitingLimit'] as
    | Partial<HistoryWaitingLimit>
    | undefined;
  if (
    (value?.limit !== 'workspace_concurrency' &&
      value?.limit !== 'webhook_concurrency') ||
    typeof value.current !== 'number' ||
    typeof value.max !== 'number'
  ) {
    return null;
  }
  return { limit: value.limit, current: value.current, max: value.max };
}

export function formatHistoryErrorMessage(
  t: HistoryStatusI18n,
  localizedMessage?: string | null
): string {
  return (
    localizedMessage ?? t['com.streamlain.streamlain.history.error-generic']()
  );
}

const historyTriggerKeys = {
  manual: 'com.streamlain.streamlain.process.schedule.manual',
  scheduled: 'com.streamlain.streamlain.process.trigger-scheduled',
  row_created: 'com.streamlain.streamlain.history.trigger-row-created',
  row_changed: 'com.streamlain.streamlain.history.trigger-row-changed',
  webhook: 'com.streamlain.streamlain.process.schedule.webhook',
  app_base: 'com.streamlain.streamlain.history.trigger-app',
  resume: 'com.streamlain.streamlain.history.trigger-resume',
} as const satisfies Record<AutomationCausation['triggerKind'], string>;

type HistoryTriggerI18n = Record<
  | (typeof historyTriggerKeys)[keyof typeof historyTriggerKeys]
  | 'com.streamlain.streamlain.history.mode-run',
  () => string
>;

/** Missing historical provenance must not be guessed from current settings. */
export function formatHistoryTriggerLabel(
  t: HistoryTriggerI18n,
  trigger?: AutomationCausation['triggerKind']
): string {
  return t[
    trigger
      ? historyTriggerKeys[trigger]
      : 'com.streamlain.streamlain.history.mode-run'
  ]();
}

const historyOutputModeKeys = {
  update_source: 'com.streamlain.automation.overview.output-mode.update_source',
  append: 'com.streamlain.automation.overview.output-mode.append',
  append_each: 'com.streamlain.automation.overview.output-mode.append_each',
  update_by_match:
    'com.streamlain.automation.overview.output-mode.update_by_match',
  upsert: 'com.streamlain.automation.overview.output-mode.upsert',
} as const;

export type HistoryOutputMode = keyof typeof historyOutputModeKeys;

type HistoryOutputModeI18n = Record<
  (typeof historyOutputModeKeys)[HistoryOutputMode],
  () => string
>;

/** A run without an output setting has no write mode to name. */
export function formatHistoryOutputModeLabel(
  t: HistoryOutputModeI18n,
  mode: HistoryOutputMode | 'not_configured'
): string | null {
  return mode === 'not_configured' ? null : t[historyOutputModeKeys[mode]]();
}

/**
 * A run of an automation without an output table writes nothing back. The
 * server still counts its processed rows, so a row count would read as rows
 * written to a table.
 */
export function historyRunWritesNothing(output: {
  mode: HistoryOutputMode | 'not_configured';
  rowsFailed: number;
  error?: string;
}): boolean {
  return (
    output.mode === 'not_configured' && output.rowsFailed === 0 && !output.error
  );
}
